import { Component, ElementRef, inject, OnDestroy, OnInit, Type, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnfrageDisplayModel } from '@/api/models/ace/xFound/Portal/Core/CAQ/Models/anfrage-display-model';
import { StateService } from '@/core/services/state.service';
import { FormularService } from '@/core/services/formular.service';
import { AbschnittDisplayModel } from '@/api/models/ace/xFound/Portal/Core/CAQ/Models/abschnitt-display-model';
import { NavigationItem } from '@/core/models/navigation-item.model';
import { AnfrageService } from '@/api/service-index';
import { ValidationFailure } from '@/api/models/FluentValidation/Results/validation-failure';
import { LoadingAnimationService } from '@/core/services/loading-animation.service';
import { PortalContextService } from '@/core/services/portal-context.service';
import { LocalizedTextDisplayModel } from '@/api/model-index';
import { I18nService } from '@/core/services/i18nService';
import { NavigationService } from '../../../../services/navigation.service';
import { DraggableSidebarService } from '@/core/services/draggable-sidebar.service';
import { AnfrageLiveSharingComponent } from '../../components/anfrage-live-sharing/anfrage-live-sharing.component';
import { ComponentService } from '@/core/services/component.service';
import { SectionService } from '@/core/services/section.service';
import { GlobalEnumerationsAceAnzeigeunterabschnitte } from '@/api/models/ace/xFound/Portal/Core/Entities/global-enumerations-ace-anzeigeunterabschnitte';
import { DetailSidepaneComponent } from '@/core/components/detail-sidepane/detail-sidepane.component';

@Component({
  selector: 'app-anfrage-detail',
  templateUrl: './anfrage-detail.component.html',
  styleUrls: ['./anfrage-detail.component.scss'],
  standalone: false
})
export class AnfrageDetailComponent implements OnInit, OnDestroy {
  protected _navItemList: NavigationItem[] | null;
  protected draggableSidebar = inject(DraggableSidebarService);
  public anfrage: AnfrageDisplayModel;
  public area: { [key: string]: ValidationFailure[] };
  public componentService = inject(ComponentService);
  public sectionService: SectionService = inject(SectionService);

  public get showProzessNavigation() {
    return this.stateService.showProzessNavigation;
  }

  public get showAbsenden(): boolean {
    return this.portalContextService.isAnfrageInUeberarbeitung() || !this.portalContextService.isAnfrageDisabled();
  }

  public get GetAbsendenText(): LocalizedTextDisplayModel[] {
    var list = this.languageservice.supportedLanguages.map(lang => {
      let text = new LocalizedTextDisplayModel();
      text.text = this.languageservice.instantForSpecificLanguage('submitdialogue.submit_send', lang)
      lang = lang.split('-')[0];
      text.localeCode = lang;
      return text;
    })
    return list;
  }

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected formularService: FormularService,
    protected stateService: StateService,
    protected anfrageService: AnfrageService,
    protected loadingAnimationService: LoadingAnimationService,
    protected portalContextService: PortalContextService,
    protected languageservice: I18nService,
    protected navigationService: NavigationService,
    protected portalStateService: StateService) {

    this.anfrage = this.portalContextService.CurrentAnfrage;

    let splittedUrl = this.router.url.split('/');
    let lastUrlSegment = splittedUrl[splittedUrl.length - 1];
    if (lastUrlSegment.includes(this.anfrage.id)) {
      if (this.anfrage.phase && this.anfrage.phase.abschnitte) {
        this.loadingAnimationService.hide();
        let link = this.formularService.getRouteObjectThrough(this.anfrage.phase?.abschnitte[0]).link.split("/");

        let sectionType = link[0];
        let sectionId = link[1];
        this.router.navigate([sectionType, sectionId], { relativeTo: this.route });
      }
    }

    if (!this.portalContextService.isAnfrageDisabled()) {
      this.anfrageService.apiAnfrageAnfrageIdValidateGet({ anfrageId: this.portalContextService.CurrentAnfrage.id }).subscribe(data => {
        this.area = data;
        this.stateService.ValidationErrors = this.area;
      });
    }
  }

  protected nullReference: AnfrageDisplayModel;
  ngOnDestroy(): void {
    //CurrentAnfrage kann vom Typ her niemals null sein, beim Verlassen sollte es aber auf null gesetzt werden, darum dieser Trick hier
    this.portalContextService.CurrentAnfrage = this.nullReference;
    this.stateService.ValidationErrors = null;
  }

  public get navItemList() {
    return this._navItemList;
  }

  protected setNavItems(abschnitte: AbschnittDisplayModel[]) {
    this._navItemList = this.navigationService.getNavItems(abschnitte);
    this._navItemList?.forEach(navItem => {
      navItem.children = this.navigationService.getNavItems(navItem.childAbschnitte);
    });
  }

  ngOnInit(): void {
    if (this.anfrage.phase?.abschnitte) {
      this.setNavItems(this.anfrage.phase.abschnitte);
    }
    this.portalContextService.AnfrageChanged.subscribe((anfrage: AnfrageDisplayModel) => {
      this.setNavItems(anfrage?.phase?.abschnitte ?? []);
    });
  }

  public get isFormEnabled(): boolean {
    return this.portalContextService.isAnfrageInUeberarbeitung() || !this.portalContextService.isAnfrageDisabled();
  }

  public isCurrentRoute(parentAbschnitt: AbschnittDisplayModel, childAbschnitt?: AbschnittDisplayModel) {
    let abschnittId: string | null = <string>this.route.firstChild?.snapshot.params['abschnittid'];

    if (!abschnittId) return false;

    // Parent is active
    if (parentAbschnitt.id == abschnittId && !childAbschnitt) {
      return true;
    }

    if (childAbschnitt && childAbschnitt.id == abschnittId) {
      return true;
    }

    // Wenn der Parent Tabs hat und der aktuelle Abschnitt ein Unterabschnitt ist, 
    // zeige den Parent als aktiv an
    if (!childAbschnitt &&
      parentAbschnitt.anzeigeUnterabschnitte === GlobalEnumerationsAceAnzeigeunterabschnitte.uberTabs &&
      parentAbschnitt.abschnitte?.some(sub => sub.id === abschnittId)) {
      return true;
    }

    return false;
  }

  public isDataprotectionRoute() {
    let urlSegments = this.router.url.split("/");

    if (urlSegments.includes("dataprotection"))
      return true;

    return false;
  }

  public isParentOpen(parentAbschnitt: AbschnittDisplayModel) {
    let abschnittId: string | null = <string>this.route.firstChild?.snapshot.params['abschnittid'];
    let isOpen = false;

    // If parent is active and has children
    if (parentAbschnitt.abschnitte && this.isCurrentRoute(parentAbschnitt)) {
      isOpen = true;
    }

    // If a child was found in parent, then show group
    let childAbschnitt = parentAbschnitt.abschnitte?.find(a => a.id == abschnittId);
    if (childAbschnitt) {
      isOpen = true;
    }

    return isOpen;
  }

  public shouldShowSubsectionsInNavigation(parentAbschnitt: AbschnittDisplayModel): boolean {
    // Wenn Unterabschnitte als Tabs angezeigt werden sollen, nicht in der Navigation anzeigen
    return parentAbschnitt.anzeigeUnterabschnitte !== GlobalEnumerationsAceAnzeigeunterabschnitte.uberTabs;
  }

  public hasNephews(navItemList: NavigationItem[] | null): boolean {
    if (!navItemList) return false;

    return navItemList.some(item => this.shouldShowSubsectionsInNavigation(item.abschnitt) && item.childAbschnitte && item.childAbschnitte.length > 0);
  }

  public isChildAbschnittNotValid(parentAbschnitt: AbschnittDisplayModel) {
    if (parentAbschnitt.abschnitte && this.portalStateService.IsEndvalidationEnabled) {
      for (let i = 0; i < parentAbschnitt.abschnitte?.length; i++) {
        if (this.getStatusFor(parentAbschnitt.abschnitte![i].id) == 0)
          return true;

      }
    }
    return false;
  }

  public getStatusFor(abschnittId: string | undefined): number {
    if (!abschnittId)
      return 0;

    if (abschnittId == 'dataprotection' && this.stateService.ValidationErrors) {
      //Spezialfall Absenden-Abschnitt: Wenn es Validierungsfehler gibt, dann ist der Abschnitt nicht valide
      if (Object.values(this.stateService.ValidationErrors).every((list) => list.length == 0)) {
        return 1;
      }

      return 0;
    }

    const navItem = this.navItemList?.find(navItem => navItem.abschnitt.id === abschnittId);

    // Wenn der Abschnitt Child-Abschnitte hat und diese als Tabs anzeigt
    if (navItem &&
      navItem.children &&
      navItem.children.length > 0 &&
      navItem.abschnitt.anzeigeUnterabschnitte === GlobalEnumerationsAceAnzeigeunterabschnitte.uberTabs) {
      // Prüfe, ob ein Child-Tab invalid ist
      for (const childNavItem of navItem.children) {
        if (this.getStatusFor(childNavItem.abschnitt.id) === 0) {
          return 0; // Wenn ein Child-Tab rot ist, ist der Parent rot
        }
      }
    }

    //Wenn es keine Validierungsfehler gibt, dann ist der Abschnitt valide
    if (!this.stateService.ValidationErrors
      || !this.stateService.ValidationErrors[abschnittId]
      || this.stateService.ValidationErrors[abschnittId].length == 0)
      return 1;

    return 0;
  }

  onAppNavigationItemClick(): void {
    if (window.innerWidth < 1024) {
      this.stateService.showProzessNavigation = false;
    }
  }

  get anfrageLiveSharingComponent(): Type<AnfrageLiveSharingComponent> {
    return this.componentService.get(AnfrageLiveSharingComponent)
  }

  public get detailSidepaneComponent(): Type<DetailSidepaneComponent> {
    return this.componentService.get(DetailSidepaneComponent);
  }
}